/**
 * ModellingPage - Modelling & Optimization tool page (Phase 5 Redesign).
 *
 * Route: /communities/:id/modelling
 *
 * MAJOR CHANGES (Phase 5):
 *   - Removed scenario selector (always day-ahead next-day optimization)
 *   - Added ForecastReadinessPanel showing forecast status
 *   - Added prerequisite check (must have battery OR flexible load)
 *   - Added BeforeAfterKPI comparison as first result section
 *   - Reordered charts: Energy Balance ABOVE Battery Schedules
 *   - Energy Balance with building dropdown (reuses EnergyBalanceChart)
 *   - Added FlexLoadGantt for flexible load scheduling
 *   - Added FlexAssetConsumptionChart for per-asset hourly consumption
 *   - Added PotentialFlexibilityChart for flexibility capacity
 *   - Renamed "Grid Export Revenue" to "Potential Grid Export Revenue"
 *   - Added Save Results button
 *   - Uses mock data with 3-second loading delay
 */

import React, { useState, useMemo } from 'react'
import { useOutletContext, useParams, Link } from 'react-router-dom'
import {
  Row, Col, Card, Form, Button, Badge, Alert, Spinner, ProgressBar,
} from 'react-bootstrap'
import Chart from 'react-apexcharts'
import { useTranslation } from 'react-i18next'
import ForecastReadinessPanel from '../components/ForecastReadinessPanel'
import BeforeAfterKPI from '../components/charts/BeforeAfterKPI'
import EnergyBalanceChart from '../components/charts/EnergyBalanceChart'
import FlexLoadGantt from '../components/charts/FlexLoadGantt'
import FlexAssetConsumptionChart from '../components/charts/FlexAssetConsumptionChart'
import PotentialFlexibilityChart from '../components/charts/PotentialFlexibilityChart'
import {
  czechEnergyBalance,
  czechBatterySchedule,
  czechOptimizationKPIs,
  czechFlexLoadSchedules,
  communityKPIs,
  czechLoadProfile,
  czechPVProfile,
  getSeason,
  estimateBuildingAnnualKwh,
} from '../data/czechEnergyProfiles'
import { isCommunityPV } from '../schemas/communitySchema'
import client from '../api/client'
import { createRun, getRunResult } from '../api/runs'
import { getValues, getDatasets } from '../api/datasets'
import { resultsAPI } from '../api/results'
import { exportOptimizationResults } from '../utils/exportCSV'
import { useAuth } from '../context/AuthContext'

function ModellingPage() {
  const { community } = useOutletContext()
  const { id: communityId } = useParams()
  const { canEdit } = useAuth()
  const { t } = useTranslation()

  // Configuration state
  const [objective, setObjective] = useState('min_cost')
  const [isRunning, setIsRunning] = useState(false)
  const [hasResults, setHasResults] = useState(false)
  const [mockResults, setMockResults] = useState(null)
  // 'loading' | 'api' | 'mock'
  const [dataSource, setDataSource] = useState('loading')
  const [balanceScope, setBalanceScope] = useState('community')

  // Community data
  const buildings = community?.buildings || []
  const communityAssets = community?.community_assets || []

  // Calculate tomorrow's date (always next day for day-ahead)
  const tomorrow = useMemo(() => {
    const date = new Date()
    date.setDate(date.getDate() + 1)
    return date.toISOString().split('T')[0]
  }, [])

  // Prerequisite checks
  const hasBattery = useMemo(() => {
    const communityBatteries = communityAssets.filter(a => a.asset_type === 'community_battery')
    const buildingBatteries = buildings.filter(b =>
      b.building_assets?.some(a => a.asset_type === 'battery')
    )
    return communityBatteries.length > 0 || buildingBatteries.length > 0
  }, [communityAssets, buildings])

  const hasFlexLoad = useMemo(() => {
    return buildings.some(b =>
      b.building_assets?.some(a =>
        ['heat_pump', 'hvac', 'ev_charger'].includes(a.asset_type) &&
        a.specs?.controllable === 'yes'
      )
    )
  }, [buildings])

  const canOptimize = hasBattery || hasFlexLoad

  // Asset summaries
  const assetSummary = useMemo(() => {
    return {
      pvFarms: communityAssets.filter(a => isCommunityPV(a)),
      windTurbines: communityAssets.filter(a => a.asset_type === 'wind_turbine'),
      communityBatteries: communityAssets.filter(a => a.asset_type === 'community_battery'),
      buildingPV: buildings.filter(b =>
        b.building_assets?.some(a => a.asset_type === 'rooftop_pv')
      ),
      buildingBatteries: buildings.filter(b =>
        b.building_assets?.some(a => a.asset_type === 'battery')
      ),
      buildingEV: buildings.filter(b =>
        b.building_assets?.some(a => a.asset_type === 'ev_charger')
      ),
      buildingHP: buildings.filter(b =>
        b.building_assets?.some(a => a.asset_type === 'heat_pump')
      ),
      buildingHVAC: buildings.filter(b =>
        b.building_assets?.some(a => a.asset_type === 'hvac')
      ),
    }
  }, [communityAssets, buildings])

  // Building list for Energy Balance dropdown
  const buildingOptions = useMemo(() => {
    return [
      { id: 'community', label: t('modelling.community_total_label') },
      ...buildings.map(b => ({ id: b.id, label: b.building_id })),
    ]
  }, [buildings, t])

  // Compute community parameters for Czech-specific results
  const kpis = useMemo(() => communityKPIs(community), [community])
  const currency = community?.currency || 'EUR'
  const EXCHANGE_RATES = { CZK: 25, EUR: 1, PLN: 4.3, HUF: 395, RON: 5, BGN: 1.96 }
  const fxRate = EXCHANGE_RATES[currency] || 1  // EUR → local currency

  /**
   * Map frontend objective names to the backend's accepted values.
   */
  const objectiveMap = {
    min_cost: 'min_cost',
    max_self_sufficiency: 'max_self_sufficiency',
    max_self_consumption: 'max_self_consumption',
    min_co2: 'min_co2',
  }

  // Track whether synthetic profiles were used (for user warning)
  const [usingSyntheticProfiles, setUsingSyntheticProfiles] = useState(false)
  const [optimizationWarnings, setOptimizationWarnings] = useState([])

  /**
   * Execute day-ahead optimization via the run management pipeline.
   *
   * Strategy:
   *   1. Try POST /api/runs { scenario_id } — the full pipeline that resolves
   *      datasets, runs the optimizer, and persists results to the DB.
   *   2. If no scenario exists, fall back to POST /v1/model/optimize with
   *      community data (but still try to load real tariffs from datasets).
   *   3. Only use Czech synthetic profiles as a last resort, with a visible warning.
   */
  const handleRunOptimization = async () => {
    setIsRunning(true)
    setHasResults(false)
    setDataSource('loading')
    setUsingSyntheticProfiles(false)
    setOptimizationWarnings([])

    const dailyKwh = kpis ? kpis.totalAnnualKwh / 365 : 193
    const pvKwp = kpis ? kpis.totalPvKwp : 60
    const battKwh = kpis ? kpis.batteryKwh : 50

    try {
      // ----------------------------------------------------------------
      // STRATEGY 1: Try the full /api/runs pipeline (scenario-based)
      // This resolves datasets, tariffs, and load profiles from the DB,
      // runs the optimizer, and persists the result.
      // ----------------------------------------------------------------
      let runSuccess = false

      // Check if there's a scenario for this community
      try {
        const scenariosResp = await client.get('/api/scenarios', {
          params: { community_id: communityId, status: 'active' }
        })
        const scenarios = Array.isArray(scenariosResp) ? scenariosResp : []
        const matchingScenario = scenarios.find(s =>
          s.scope === 'day_ahead' || s.objective === (objectiveMap[objective] || 'min_cost')
        ) || scenarios[0]

        if (matchingScenario) {
          const runResponse = await createRun({ scenario_id: matchingScenario.id })
          const runId = runResponse.id || runResponse.run_id

          if (runResponse.status === 'completed' || runResponse.status === 'optimal') {
            // Fetch the full result
            const result = await getRunResult(runId)
            setDataSource('api')
            setOptimizationWarnings(result.warnings || runResponse.warnings || [])
            setMockResults({
              baselineKPIs: result.baselineKPIs || result.baseline_kpis,
              optimizedKPIs: result.optimizedKPIs || result.optimized_kpis,
              energyBalance: result.energyBalance || result.energy_balance,
              batterySchedule: result.batterySchedule || result.battery_schedule,
              flexLoads: result.flexLoads || result.flex_loads || [],
              flexAssetConsumption: null,
              potentialFlexibility: result.potentialFlexibility || result.potential_flexibility || null,
            })
            setHasResults(true)
            runSuccess = true
          } else if (runResponse.status === 'failed') {
            console.warn('Run failed:', runResponse.warnings)
            setOptimizationWarnings(runResponse.warnings || [])
          }
        }
      } catch (runError) {
        console.warn('Run pipeline unavailable, falling back to direct optimizer:', runError.message)
      }

      // ----------------------------------------------------------------
      // STRATEGY 2: Direct optimizer call with community data
      // Used when no scenario exists or the run pipeline is unavailable.
      // Still tries to load real tariff data from datasets.
      // ----------------------------------------------------------------
      if (!runSuccess) {
        let usingFallbackProfiles = false
        let buildingsWithRealData = 0

        // Per-building load is now computed individually via estimateBuildingAnnualKwh()
        const perBuildingKwp = pvKwp / Math.max(1, buildings.length)

        // ----------------------------------------------------------------
        // Load all available datasets for this community upfront
        // so we can match them to buildings by building_id
        // ----------------------------------------------------------------
        let allDatasets = []
        try {
          const dsResp = await getDatasets({ community_id: communityId })
          allDatasets = Array.isArray(dsResp) ? dsResp : []
        } catch (dsErr) {
          console.warn('Could not fetch dataset list:', dsErr.message)
        }

        /**
         * Load 24 hourly values from a dataset for the target optimization date.
         *
         * Strategy: request values matching the target month/day so we get
         * seasonally representative data. Datasets typically hold a full year
         * (8760 hours, starting 2025-01-01). If the target date falls outside
         * the dataset range we try the same month/day from the dataset year.
         * Falls back to first 24 values only as a last resort.
         *
         * @param {string} datasetId
         * @param {string} [targetDate] - ISO date string (YYYY-MM-DD), defaults to tomorrow
         * @returns {number[]|null} 24-element array of kW values, or null
         */
        const loadHourlyValues = async (datasetId, targetDate) => {
          if (!datasetId) return null
          const dateStr = targetDate || tomorrow

          try {
            // Try loading values for the matching month/day in 2025
            // (dataset year). This gives seasonally representative data.
            const tgt = new Date(dateStr + 'T00:00:00Z')
            const datasetYear = 2025 // datasets contain full-year 2025 data
            const startISO = `${datasetYear}-${String(tgt.getUTCMonth() + 1).padStart(2, '0')}-${String(tgt.getUTCDate()).padStart(2, '0')}T00:00:00Z`
            const endDate = new Date(startISO)
            endDate.setUTCHours(23, 59, 59)
            const endISO = endDate.toISOString()

            const resp = await getValues(datasetId, {
              start_time: startISO,
              end_time: endISO,
              limit: 24,
            })
            const rawValues = resp?.values || (Array.isArray(resp) ? resp : [])
            if (rawValues.length >= 24) {
              return rawValues.slice(0, 24).map(v =>
                typeof v === 'number' ? v : (v?.value ?? v?.consumption_kwh ?? 0)
              )
            }

            // Fallback: if date-filtered query returned too few values,
            // try without date filter (first 24 values)
            if (rawValues.length < 24) {
              console.warn(`Date-filtered dataset ${datasetId} returned only ${rawValues.length} values for ${startISO}; trying unfiltered`)
              const fallbackResp = await getValues(datasetId, { limit: 24 })
              const fallbackValues = fallbackResp?.values || (Array.isArray(fallbackResp) ? fallbackResp : [])
              if (fallbackValues.length >= 24) {
                return fallbackValues.slice(0, 24).map(v =>
                  typeof v === 'number' ? v : (v?.value ?? v?.consumption_kwh ?? 0)
                )
              }
            }
          } catch (e) {
            console.warn(`Failed to load values from dataset ${datasetId}:`, e.message)
          }
          return null
        }

        // ----------------------------------------------------------------
        // Load saved forecast results to use as fallback before synthetics.
        // Build a map of asset_name -> forecast data for per-building matching.
        // ----------------------------------------------------------------
        const savedLoadForecasts = {}  // keyed by asset name (building_id)
        const savedPVForecasts = {}    // keyed by asset name — per-asset PV generation forecasts
        const savedWindForecasts = {}  // keyed by asset name — per-asset wind generation forecasts
        try {
          const savedResults = await resultsAPI.list(communityId)
          const allResults = Array.isArray(savedResults) ? savedResults : []

          // Collect ALL saved load forecasts, keyed by asset name
          const loadResults = allResults.filter(r =>
            r.result_type === 'forecast' && r.parameters?.forecast_type === 'load' && r.parameters?.forecast_mode === 'short_term'
          )
          // Fetch full data for each (most recent per asset wins due to sort order)
          for (const lr of loadResults) {
            const assetName = lr.parameters?.asset
            if (!assetName || savedLoadForecasts[assetName]) continue  // skip duplicates
            try {
              const full = await resultsAPI.get(communityId, lr.id)
              if (full?.result_data?.forecast?.length >= 24) {
                savedLoadForecasts[assetName] = full.result_data.forecast.slice(0, 24)
              }
            } catch (e) { /* skip failed fetches */ }
          }
          if (Object.keys(savedLoadForecasts).length > 0) {
            console.info(`Loaded ${Object.keys(savedLoadForecasts).length} saved load forecasts:`, Object.keys(savedLoadForecasts))
          }

          // Generation forecasts — collect ALL per-asset PV and wind forecasts
          const genResults = allResults.filter(r =>
            r.result_type === 'forecast' && r.parameters?.forecast_type === 'generation' && r.parameters?.forecast_mode === 'short_term'
          )
          for (const gr of genResults) {
            const assetName = gr.parameters?.asset
            const category = gr.parameters?.asset_category || 'pv' // backward compat: no category = PV
            if (!assetName) continue
            const targetMap = category === 'wind' ? savedWindForecasts : savedPVForecasts
            if (targetMap[assetName]) continue // skip duplicates (most recent wins)
            try {
              const full = await resultsAPI.get(communityId, gr.id)
              if (full?.result_data?.forecast?.length >= 24) {
                targetMap[assetName] = full.result_data.forecast.slice(0, 24)
                console.info(`Loaded saved ${category} generation forecast for: ${assetName}`)
              }
            } catch (e) { /* skip */ }
          }
          if (Object.keys(savedPVForecasts).length > 0) {
            console.info(`Loaded ${Object.keys(savedPVForecasts).length} saved PV generation forecasts:`, Object.keys(savedPVForecasts))
          }
          if (Object.keys(savedWindForecasts).length > 0) {
            console.info(`Loaded ${Object.keys(savedWindForecasts).length} saved wind generation forecasts:`, Object.keys(savedWindForecasts))
          }
        } catch (err) {
          console.warn('Could not load saved forecast results:', err.message)
        }

        // ----------------------------------------------------------------
        // Build per-building optimizer input, loading real data when available
        // Priority: 1) Dataset values 2) Saved forecast results 3) Synthetic
        // ----------------------------------------------------------------
        const optimizerBuildings = await Promise.all(buildings.map(async (b, idx) => {
          const bid = b.building_id || b.id || `building_${idx}`
          const bAssets = b.building_assets || []

          // Find load dataset for this building
          const buildingDatasets = b.datasets || []
          const loadDataset = buildingDatasets.find(d => d.kind === 'load' || d.dataset_type?.includes('load'))
            || allDatasets.find(d => d.building_id === b.id && (d.kind === 'load' || d.dataset_type?.includes('load')))

          // Try to load real load profile from dataset
          let loadProfile = await loadHourlyValues(loadDataset?.id)
          if (loadProfile) {
            buildingsWithRealData++
          } else if (savedLoadForecasts[bid]) {
            // Use this building's own saved forecast result
            loadProfile = savedLoadForecasts[bid]
            buildingsWithRealData++
          } else if (savedLoadForecasts['Entire Community']) {
            // Fallback: split community-level forecast equally across buildings
            loadProfile = savedLoadForecasts['Entire Community'].map(v => +(v / Math.max(1, buildings.length)).toFixed(2))
          } else {
            // Fallback to synthetic profile using per-building annual load estimate
            const buildingAnnualKwh = estimateBuildingAnnualKwh(b, community)
            const buildingDailyKwh = buildingAnnualKwh / 365
            loadProfile = czechLoadProfile(buildingDailyKwh, 42 + idx, community?.metadata?.country)
            usingFallbackProfiles = true
          }

          // Find PV dataset or use synthetic
          const rooftopPV = bAssets.find(a => a.asset_type === 'rooftop_pv')
          const buildingKwp = rooftopPV?.specs?.capacity_kwp || perBuildingKwp
          let pvProfile = null
          if (rooftopPV) {
            // Check if there's a PV dataset for this building
            const pvDataset = buildingDatasets.find(d => d.kind === 'pv' || d.dataset_type?.includes('pv'))
              || allDatasets.find(d => d.building_id === b.id && (d.kind === 'pv' || d.dataset_type?.includes('pv')))
            pvProfile = await loadHourlyValues(pvDataset?.id)
            if (!pvProfile) {
              // Try per-asset saved forecast: match by building rooftop PV name
              const rooftopName = `${bid} Rooftop PV`
              if (savedPVForecasts[rooftopName]) {
                pvProfile = savedPVForecasts[rooftopName]
                console.info(`Using saved per-asset PV forecast for building ${bid}`)
              }
            }
            if (!pvProfile) {
              pvProfile = czechPVProfile(buildingKwp, getSeason(tomorrow), 43 + idx, community?.metadata?.latitude)
            }
          }

          const entry = {
            building_id: bid,
            load_kwh: loadProfile,
            pv_gen_kw: pvProfile,
          }

          const battAsset = bAssets.find(a => a.asset_type === 'battery')
          if (battAsset) {
            const bSpec = battAsset.specs || {}
            entry.battery = {
              capacity_kwh: bSpec.capacity_kwh || 10,
              p_charge_kw: bSpec.p_charge_kw || bSpec.max_charge_kw || 3,
              p_discharge_kw: bSpec.p_discharge_kw || bSpec.max_discharge_kw || 3,
              efficiency_charge: bSpec.efficiency_charge || 0.95,
              efficiency_discharge: bSpec.efficiency_discharge || 0.95,
              soc_min_pct: bSpec.soc_min_pct || 10,
              soc_max_pct: bSpec.soc_max_pct || 90,
              initial_soc_pct: 50,
            }
          }

          // Helper: controllable defaults to 'yes' when not explicitly set to 'no'
          const isControllable = (a) => a.specs?.controllable !== 'no'

          const hpAsset = bAssets.find(a => a.asset_type === 'heat_pump' && isControllable(a))
          if (hpAsset) {
            const s = hpAsset.specs || {}
            entry.heat_pump = {
              rated_power_kw: s.capacity_kw || s.rated_power_kw || 3,
              cop: s.cop || 3.0,
              eer: s.eer || 2.5,
              operating_mode: s.operating_mode || s.mode || 'heating',
              thermal_mass: s.thermal_mass || s.thermal_mass_kwh_per_c || 'medium',
              min_comfort_temp: s.min_comfort_temp || s.t_min_c || 20,
              max_comfort_temp: s.max_comfort_temp || s.t_max_c || 24,
              initial_indoor_temp: s.initial_indoor_temp || s.t_initial_c || 21,
              controllable: true,
            }
          }

          const hvacAsset = bAssets.find(a => a.asset_type === 'hvac' && isControllable(a))
          if (hvacAsset && !hpAsset) {
            const s = hvacAsset.specs || {}
            entry.hvac = {
              rated_power_kw: s.capacity_kw || s.rated_power_kw || 5,
              cop: s.cop || s.cop_heating || 2.5,
              eer: s.eer || s.cop_cooling || 2.0,
              operating_mode: s.operating_mode || s.mode || 'heating',
              thermal_mass: s.thermal_mass || s.thermal_mass_kwh_per_c || 'medium',
              min_comfort_temp: s.min_comfort_temp || s.t_min_c || 20,
              max_comfort_temp: s.max_comfort_temp || s.t_max_c || 24,
              initial_indoor_temp: s.initial_indoor_temp || s.t_initial_c || 21,
              controllable: true,
            }
          }

          const evAsset = bAssets.find(a => a.asset_type === 'ev_charger' && isControllable(a))
          if (evAsset) {
            const s = evAsset.specs || {}
            entry.ev_charger = {
              max_power_kw: s.max_power_kw || 11,
              num_evs: s.num_evs || 1,
              daily_energy_per_ev_kwh: s.daily_energy_kwh || s.daily_energy_per_ev_kwh || 25,
              availability_start: s.availability_start || s.arrival_hour || 18,
              availability_end: s.availability_end || s.departure_hour || 8,
              ev_battery_capacity_kwh: s.ev_battery_capacity_kwh || s.battery_capacity_kwh || 60,
            }
          }

          return entry
        }))

        // Community-level assets — also try loading PV from dataset
        const pvFarm = communityAssets.find(a => isCommunityPV(a))
        const windTurbine = communityAssets.find(a => a.asset_type === 'wind_turbine')
        const commBattery = communityAssets.find(a => a.asset_type === 'community_battery')

        let communityPV = null
        if (pvFarm) {
          const farmKwp = pvFarm.specs?.capacity_kwp || 0
          // Try loading community PV generation from dataset
          const pvDataset = allDatasets.find(d =>
            !d.building_id && (d.kind === 'pv' || d.dataset_type?.includes('pv_generation'))
          )
          let pvGenProfile = await loadHourlyValues(pvDataset?.id)
          if (!pvGenProfile) {
            // Try per-asset saved forecast: match by farm name or any PV forecast
            const farmName = pvFarm.name || 'Solar PV Farm'
            if (savedPVForecasts[farmName]) {
              pvGenProfile = savedPVForecasts[farmName]
              console.info(`Using saved per-asset PV forecast for community farm: ${farmName}`)
            } else {
              // Aggregate all saved PV forecasts as fallback (sum across all PV assets)
              const allPVArrays = Object.values(savedPVForecasts)
              if (allPVArrays.length > 0) {
                pvGenProfile = allPVArrays[0].map((_, t) =>
                  +allPVArrays.reduce((sum, arr) => sum + (arr[t] || 0), 0).toFixed(2)
                )
                console.info(`Aggregated ${allPVArrays.length} saved PV forecasts for community PV farm`)
              }
            }
          }
          if (!pvGenProfile) {
            pvGenProfile = czechPVProfile(farmKwp, getSeason(tomorrow), 99, community?.metadata?.latitude)
          }
          communityPV = {
            capacity_kwp: farmKwp,
            gen_kw: pvGenProfile,
          }
        }

        let communityWind = null
        if (windTurbine) {
          const windKw = windTurbine.specs?.capacity_kw || 0
          // Try loading community wind generation from dataset
          const windDataset = allDatasets.find(d =>
            !d.building_id && (d.kind === 'wind' || d.dataset_type?.includes('wind_generation'))
          )
          let windGenProfile = await loadHourlyValues(windDataset?.id)
          if (!windGenProfile) {
            // Try per-asset saved wind forecast
            const turbineName = windTurbine.name || 'Wind Turbine'
            if (savedWindForecasts[turbineName]) {
              windGenProfile = savedWindForecasts[turbineName]
              console.info(`Using saved per-asset wind forecast for: ${turbineName}`)
            } else {
              // Aggregate all saved wind forecasts
              const allWindArrays = Object.values(savedWindForecasts)
              if (allWindArrays.length > 0) {
                windGenProfile = allWindArrays[0].map((_, t) =>
                  +allWindArrays.reduce((sum, arr) => sum + (arr[t] || 0), 0).toFixed(2)
                )
                console.info(`Aggregated ${allWindArrays.length} saved wind forecasts`)
              }
            }
          }
          if (!windGenProfile) {
            // Fallback to synthetic wind profile
            windGenProfile = Array.from({ length: 24 }, (_, t) =>
              parseFloat((windKw * (0.3 + 0.2 * Math.sin(t * 0.5))).toFixed(2))
            )
          }
          communityWind = {
            capacity_kw: windKw,
            gen_kw: windGenProfile,
          }
        }

        let communityBattery = null
        if (commBattery) {
          const cbSpec = commBattery.specs || {}
          communityBattery = {
            capacity_kwh: cbSpec.capacity_kwh || 50,
            p_charge_kw: cbSpec.p_charge_kw || cbSpec.max_charge_kw || 15,
            p_discharge_kw: cbSpec.p_discharge_kw || cbSpec.max_discharge_kw || 15,
            efficiency_charge: cbSpec.efficiency_charge || 0.95,
            efficiency_discharge: cbSpec.efficiency_discharge || 0.95,
            soc_min_pct: cbSpec.soc_min_pct || 10,
            soc_max_pct: cbSpec.soc_max_pct || 90,
            initial_soc_pct: 50,
          }
        }

        // Log data sourcing summary
        console.info(
          `Optimizer input: ${buildingsWithRealData}/${buildings.length} buildings with real data,`,
          `${usingFallbackProfiles ? 'some synthetic fallback used' : 'all real data'}`
        )

        // ----------------------------------------------------------------
        // Tariff: try to load from community tariff datasets first
        // ----------------------------------------------------------------
        let tariffImport, tariffExport
        let tariffSource = 'default'
        const datasets = community?.community_datasets || community?.datasets || []
        const importTariffDs = datasets.find(d => d.dataset_type === 'energy_tariff_data' || d.kind === 'tariff')
        const exportTariffDs = datasets.find(d => d.dataset_type === 'feed_in_tariff_data')

        // Use the same date-aware loading for tariffs (reuse loadHourlyValues)
        try {
          if (importTariffDs?.id) {
            const tariffValues = await loadHourlyValues(importTariffDs.id, tomorrow)
            if (tariffValues && tariffValues.length >= 24) {
              tariffImport = tariffValues
              tariffSource = 'dataset'
            }
          }
          if (exportTariffDs?.id) {
            const exportValues = await loadHourlyValues(exportTariffDs.id, tomorrow)
            if (exportValues && exportValues.length >= 24) {
              tariffExport = exportValues
            }
          }
        } catch (tariffError) {
          console.warn('Could not load tariff datasets, using defaults:', tariffError.message)
        }

        // Fallback: derive tariffs from community metadata or use sensible defaults
        if (!tariffImport) {
          const meta = community?.metadata || {}
          const importRate = meta.import_tariff
            ? meta.import_tariff / (currency === 'CZK' ? 25 : 1)  // Convert to EUR if CZK
            : (currency === 'CZK' ? 0.22 : 0.15)
          tariffImport = Array.from({ length: 24 }, (_, t) => {
            if ((t >= 7 && t <= 9) || (t >= 17 && t <= 21)) return parseFloat((importRate * 1.2).toFixed(4))
            return importRate
          })
        }
        if (!tariffExport) {
          const meta = community?.metadata || {}
          const exportRate = meta.export_tariff
            ? meta.export_tariff / (currency === 'CZK' ? 25 : 1)
            : (currency === 'CZK' ? 0.08 : 0.05)
          tariffExport = Array(24).fill(exportRate)
        }

        // CO2 factor: derive from community metadata or use country defaults
        const co2Factor = community?.metadata?.co2_factor_kg_kwh
          || (community?.metadata?.country === 'Italy' ? 0.233
            : community?.metadata?.country === 'Germany' ? 0.350
            : 0.4)

        setUsingSyntheticProfiles(usingFallbackProfiles)

        // Try to load outdoor temperature from weather dataset
        let temperatureProfile = null
        const weatherDataset = allDatasets.find(d =>
          d.dataset_type?.includes('weather') || d.dataset_type?.includes('temperature')
        )
        if (weatherDataset?.id) {
          try {
            temperatureProfile = await loadHourlyValues(weatherDataset.id, tomorrow)
          } catch (weatherErr) {
            console.warn('Could not load weather data, using regional profiles:', weatherErr.message)
          }
        }

        const optimizerRequest = {
          horizon_hours: 24,
          time_resolution_hours: 1.0,
          objective: objectiveMap[objective] || 'min_cost',
          co2_factor_kg_kwh: co2Factor,
          buildings: optimizerBuildings,
          community_pv: communityPV,
          community_wind: communityWind,
          community_battery: communityBattery,
          tariff_import: tariffImport,
          tariff_export: tariffExport,
          scope: 'community',
          solver_time_limit_seconds: 30,
          season: getSeason(tomorrow),
          latitude: community?.metadata?.latitude || null,
          country: community?.metadata?.country || null,
          temperature_c: temperatureProfile || null,
        }

        const response = await client.post('/v1/model/optimize', optimizerRequest, {
          timeout: 120000,
        })

        if (response.status === 'validation_error') {
          // Pre-solve validation caught impossible inputs — show errors and fall back
          setOptimizationWarnings(
            (response.validation_errors || []).map(msg => ({
              type: 'validation_error',
              message: msg,
              severity: 'high',
            }))
          )
          setDataSource('mock')
          _applyMockResults({ dailyKwh, pvKwp, battKwh })
        } else if (response.status === 'optimal') {
          setDataSource(tariffSource === 'dataset' && !usingFallbackProfiles ? 'api' : 'partial')
          if (tariffSource !== 'dataset') {
            setOptimizationWarnings(prev => [...prev, {
              type: 'default_tariff',
              message: t('modelling.default_tariff_warning'),
              severity: 'medium',
            }])
          }
          // Extract flex load warnings from optimizer
          const flexWarnings = response.flexLoadWarnings || []
          if (flexWarnings.length > 0) {
            setOptimizationWarnings(prev => [...prev, ...flexWarnings])
          }

          // Build per-building energy balance map from optimizer's schedules.
          // Key by both building_id (string) and building UUID for flexible lookup.
          const perBuildingEB = {}
          const perBldg = response.schedules?.per_building || []
          for (const bs of perBldg) {
            const ebData = {
              load: bs.load_kw || bs.load_kwh || [],
              pv: bs.pv_used_kw || [],
              commAllocation: bs.comm_gen_allocated_kw || [],
              flexLoad: bs.flex_load_kw || [],
              wind: [],
              gridImport: bs.grid_import_kw || [],
              gridExport: bs.grid_export_kw || [],
              batteryCharge: bs.battery_charge_kw || [],
              batteryDischarge: bs.battery_discharge_kw || [],
              hours: Array.from({ length: 24 }, (_, i) => i),
            }
            // Key by building_id string (e.g. "hotel_hirsch")
            perBuildingEB[bs.building_id] = ebData
            // Also key by building UUID for scope selector compatibility
            const matchingBuilding = buildings.find(b => b.building_id === bs.building_id)
            if (matchingBuilding) {
              perBuildingEB[matchingBuilding.id] = ebData
            }
          }

          // Build flexAssetConsumption from optimizer's flexLoads for the per-asset chart
          const flexAssetConsumption = (response.flexLoads || []).map((fl, idx) => ({
            id: `${fl.building_id}_${fl.asset_type}_${idx}`,
            name: `${fl.asset_name} - ${fl.building_id}`,
            type: fl.asset_type,
            power: fl.power_kw || [],
            temperature: fl.temperature_c || null,
          }))

          setMockResults({
            baselineKPIs: response.baselineKPIs,
            optimizedKPIs: response.optimizedKPIs,
            energyBalance: response.energyBalance,
            perBuildingEB,
            batterySchedule: response.batterySchedule,
            flexLoads: response.flexLoads || [],
            flexAssetConsumption: flexAssetConsumption.length > 0 ? flexAssetConsumption : null,
            potentialFlexibility: response.potentialFlexibility || null,
          })
          setHasResults(true)

          // Persist result via resultsAPI so PastResults page can find it
          try {
            await resultsAPI.save(communityId, {
              result_type: 'optimization',
              name: `Optimization (${objective}) - ${tomorrow}`,
              parameters: { objective, date: tomorrow, tariff_source: tariffSource },
              result_data: {
                baselineKPIs: response.baselineKPIs,
                optimizedKPIs: response.optimizedKPIs,
                energyBalance: response.energyBalance,
                batterySchedule: response.batterySchedule,
                flexLoads: response.flexLoads || [],
                flexLoadWarnings: response.flexLoadWarnings || [],
                schedules: response.schedules || null,
              },
            })
          } catch (saveErr) {
            console.warn('Auto-save of optimization result failed:', saveErr.message)
          }
        } else {
          console.warn('Optimizer returned non-optimal status:', response.status, response.error)
          setDataSource('mock')
          _applyMockResults({ dailyKwh, pvKwp, battKwh })
        }
      }

    } catch (error) {
      console.error('Optimization API call failed, falling back to mock data:', error)
      setDataSource('mock')
      _applyMockResults({ dailyKwh, pvKwp, battKwh })
    } finally {
      setIsRunning(false)
    }
  }

  /**
   * Apply Czech mock optimization results as a fallback when the real API
   * is unavailable or returns an error.
   */
  const _applyMockResults = ({ dailyKwh, pvKwp, battKwh }) => {
    const optKPIs = czechOptimizationKPIs({ dailyKwh, pvKwp, currency, baselineSCR: kpis?.selfConsumption })
    const ebData = czechEnergyBalance({ dailyKwh, pvKwp, batteryKwh: battKwh, season: getSeason(tomorrow), seed: 99, country: community?.metadata?.country, latitude: community?.metadata?.latitude })
    const bsData = czechBatterySchedule(battKwh, 99)
    const flexData = czechFlexLoadSchedules(buildings)

    setMockResults({
      baselineKPIs: optKPIs.baselineKPIs,
      optimizedKPIs: optKPIs.optimizedKPIs,
      energyBalance: ebData,
      batterySchedule: bsData,
      flexLoads: flexData,
      flexAssetConsumption: null,
      potentialFlexibility: null,
    })
    setHasResults(true)
  }

  const [isSaving, setIsSaving] = useState(false)
  const [saveStatus, setSaveStatus] = useState(null)  // null | 'success' | 'error'

  const handleSaveResults = async () => {
    if (!hasResults || !mockResults) return
    // communityId comes from useParams() — the real community UUID in the URL
    if (!communityId) {
      setSaveStatus('error')
      return
    }
    setIsSaving(true)
    setSaveStatus(null)
    try {
      await resultsAPI.save(communityId, {
        result_type: 'optimization',
        name: `Optimization (${objective}) - ${tomorrow}`,
        parameters: { objective, date: tomorrow },
        result_data: {
          baselineKPIs: mockResults.baselineKPIs,
          optimizedKPIs: mockResults.optimizedKPIs,
          energyBalance: mockResults.energyBalance,
          batterySchedule: mockResults.batterySchedule,
          flexLoads: mockResults.flexLoads || [],
          flexLoadWarnings: [],
          schedules: null,
        },
      })
      setSaveStatus('success')
      // Auto-clear success message after 3 seconds
      setTimeout(() => setSaveStatus(null), 3000)
    } catch (error) {
      console.error('Failed to save optimization results:', error)
      setSaveStatus('error')
    } finally {
      setIsSaving(false)
    }
  }

  const handleExportCSV = () => {
    if (!hasResults || !mockResults) return
    exportOptimizationResults(mockResults, tomorrow, currency)
  }

  /** Objectives with descriptions */
  const objectives = [
    {
      value: 'min_cost',
      label: t('modelling.obj_min_cost'),
      description: t('modelling.obj_min_cost_desc'),
    },
    {
      value: 'max_self_sufficiency',
      label: t('modelling.obj_max_self_sufficiency'),
      description: t('modelling.obj_max_self_sufficiency_desc'),
    },
    {
      value: 'max_self_consumption',
      label: t('modelling.obj_max_self_consumption'),
      description: t('modelling.obj_max_self_consumption_desc'),
    },
    {
      value: 'min_co2',
      label: t('modelling.obj_min_co2'),
      description: t('modelling.obj_min_co2_desc'),
    },
  ]

  return (
    <div>
      {/* Page Header */}
      <div className="tool-page-header">
        <h1 className="tool-page-title">{t('modelling.page_title')}</h1>
        <p className="tool-page-subtitle">
          {community?.name
            ? t('modelling.page_subtitle_named', { name: community.name })
            : t('modelling.page_subtitle_default')}
        </p>
      </div>

      <Row className="g-4">
        {/* Setup Panel (Left) */}
        <Col lg={5} xl={4}>
          {/* Optimization Setup */}
          <div className="setup-panel">
            <h5 className="setup-panel-title">{t('modelling.setup_title')}</h5>

            {/* Optimization Date (read-only, always tomorrow) */}
            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: 500, fontSize: 'var(--font-size-sm)' }}>
                {t('modelling.opt_date')}
              </Form.Label>
              <Form.Control
                type="text"
                value={tomorrow}
                readOnly
                style={{ backgroundColor: 'var(--color-neutral-100)', color: 'var(--color-neutral-700)' }}
              />
              <Form.Text className="text-muted">
                {t('modelling.opt_date_help')}
              </Form.Text>
            </Form.Group>

            {/* Optimization Objective */}
            <Form.Group className="mb-4">
              <Form.Label style={{ fontWeight: 500, fontSize: 'var(--font-size-sm)' }}>
                {t('modelling.opt_objective')}
              </Form.Label>
              {objectives.map((obj) => (
                <Form.Check
                  key={obj.value}
                  type="radio"
                  id={`obj-${obj.value}`}
                  name="objective"
                  label={
                    <div>
                      <span style={{ fontWeight: 500 }}>{obj.label}</span>
                      <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)' }}>
                        {obj.description}
                      </div>
                    </div>
                  }
                  checked={objective === obj.value}
                  onChange={() => setObjective(obj.value)}
                  className="mb-2"
                />
              ))}
            </Form.Group>

            {/* Forecast Readiness Panel */}
            <ForecastReadinessPanel community={community} targetDate={tomorrow} />

            {/* Prerequisite Check */}
            {!canOptimize && (
              <Alert variant="warning" className="mt-3" style={{ fontSize: 'var(--font-size-sm)' }}>
                <strong>{t('modelling.not_enough_resources')}</strong>
                <br />
                {t('modelling.not_enough_text')}{' '}
                <Link to={`/communities/${communityId}/buildings`}>{t('modelling.link_buildings')}</Link> or{' '}
                <Link to={`/communities/${communityId}/assets`}>{t('modelling.link_community_assets')}</Link>.
              </Alert>
            )}

            {/* Run Button */}
            <Button
              onClick={handleRunOptimization}
              disabled={isRunning || !canOptimize}
              style={{
                backgroundColor: 'var(--color-accent)',
                borderColor: 'var(--color-accent)',
                fontWeight: 600,
                width: '100%',
                padding: 'var(--space-3) var(--space-4)',
                marginTop: 'var(--space-4)',
              }}
            >
              {isRunning ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {t('modelling.running_optimization')}
                </>
              ) : (
                t('modelling.btn_run_optimization')
              )}
            </Button>
          </div>

          {/* Community Assets Summary */}
          <div className="setup-panel">
            <h5 className="setup-panel-title">{t('modelling.community_assets_title')}</h5>

            {communityAssets.length === 0 && buildings.length === 0 ? (
              <Alert variant="info" style={{ fontSize: 'var(--font-size-sm)' }}>
                {t('modelling.no_assets_configured')}{' '}
                <Link to={`/communities/${communityId}/assets`}>{t('modelling.link_assets_buildings')}</Link>.
              </Alert>
            ) : (
              <div className="d-flex flex-column gap-2">
                {/* PV Farms */}
                {assetSummary.pvFarms.map(asset => (
                  <div key={asset.id} className="asset-summary-card">
                    <div className="asset-summary-icon"
                      style={{ backgroundColor: '#FFF9E6', color: 'var(--color-solar)' }}>
                      &#x2600;
                    </div>
                    <div className="asset-summary-info">
                      <p className="asset-summary-name">{asset.name || t('modelling.asset_solar_pv_farm')}</p>
                      <p className="asset-summary-detail">
                        {asset.specs?.capacity_kwp || '--'} kWp
                      </p>
                    </div>
                  </div>
                ))}

                {/* Wind Turbines */}
                {assetSummary.windTurbines.map(asset => (
                  <div key={asset.id} className="asset-summary-card">
                    <div className="asset-summary-icon"
                      style={{ backgroundColor: '#EBF2FC', color: 'var(--color-wind)' }}>
                      &#x1F4A8;
                    </div>
                    <div className="asset-summary-info">
                      <p className="asset-summary-name">{asset.name || t('modelling.asset_wind_turbine')}</p>
                      <p className="asset-summary-detail">
                        {asset.specs?.capacity_kw || '--'} kW
                      </p>
                    </div>
                  </div>
                ))}

                {/* Community Batteries */}
                {assetSummary.communityBatteries.map(asset => (
                  <div key={asset.id} className="asset-summary-card">
                    <div className="asset-summary-icon"
                      style={{ backgroundColor: '#F3EBF8', color: 'var(--color-battery)' }}>
                      &#x1F50B;
                    </div>
                    <div className="asset-summary-info">
                      <p className="asset-summary-name">{asset.name || t('modelling.asset_community_battery')}</p>
                      <p className="asset-summary-detail">
                        {asset.specs?.capacity_kwh || '--'} kWh | {t('modelling.kw_charge_label', { value: asset.specs?.p_charge_kw || '--' })}
                      </p>
                    </div>
                  </div>
                ))}

                {/* Building-level asset counts */}
                <hr className="my-2" />
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)' }}>
                  <strong>{t('modelling.building_level')}</strong>{' '}
                  {t('modelling.count_rooftop_pv', { count: assetSummary.buildingPV.length })} |{' '}
                  {t('modelling.count_batteries', { count: assetSummary.buildingBatteries.length })} |{' '}
                  {t('modelling.count_ev_chargers', { count: assetSummary.buildingEV.length })} |{' '}
                  {t('modelling.count_heat_pumps', { count: assetSummary.buildingHP.length })} |{' '}
                  {t('modelling.count_hvac', { count: assetSummary.buildingHVAC.length })}
                </div>
              </div>
            )}
          </div>
        </Col>

        {/* Results Area (Right) */}
        <Col lg={7} xl={8}>
          {/* No result yet */}
          {!hasResults && !isRunning && (
            <div className="results-panel">
              <div className="chart-placeholder" style={{ minHeight: '500px' }}>
                <div className="chart-placeholder-icon">&#x26A1;</div>
                <div className="chart-placeholder-text">
                  {t('modelling.results_placeholder_title')}
                </div>
                <div className="chart-placeholder-subtext">
                  {t('modelling.results_placeholder_text')}
                </div>
                <div style={{ marginTop: 'var(--space-4)', maxWidth: '500px', textAlign: 'center' }}>
                  <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-500)' }}>
                    {t('modelling.results_placeholder_subtext')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Running */}
          {isRunning && (
            <div className="results-panel text-center" style={{ padding: 'var(--space-16) var(--space-8)' }}>
              <Spinner animation="border" style={{ color: 'var(--color-accent)', width: '3rem', height: '3rem' }} />
              <h5 className="mt-4" style={{ color: 'var(--color-neutral-800)' }}>
                {t('modelling.optimizing_title')}
              </h5>
              <p style={{ color: 'var(--color-neutral-600)', fontSize: 'var(--font-size-sm)', maxWidth: '400px', margin: '0 auto' }}>
                {t('modelling.optimizing_text', { buildings: buildings.length, assets: communityAssets.length })}
              </p>
              <ProgressBar
                animated
                now={100}
                className="mt-4"
                style={{ maxWidth: '300px', margin: '0 auto', height: '6px' }}
              />
              <p className="mt-2" style={{ color: 'var(--color-neutral-500)', fontSize: 'var(--font-size-xs)' }}>
                {t('modelling.solve_time_hint')}
              </p>
            </div>
          )}

          {/* Completed Results */}
          {hasResults && mockResults && (
            <div>
              {dataSource === 'mock' && (
                <div className="alert alert-warning d-flex align-items-center mb-3" role="alert">
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  <div>
                    <strong>{t('modelling.sample_data_label')}</strong> {t('modelling.sample_data_text')}
                  </div>
                </div>
              )}

              {dataSource === 'partial' && (
                <div className="alert alert-info d-flex align-items-center mb-3" role="alert">
                  <i className="bi bi-info-circle-fill me-2"></i>
                  <div>
                    <strong>{t('modelling.partial_data_label')}</strong> {t('modelling.partial_data_text')}
                  </div>
                </div>
              )}

              {usingSyntheticProfiles && dataSource !== 'mock' && (
                <div className="alert alert-warning d-flex align-items-center mb-3" role="alert">
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  <div>
                    <strong>{t('modelling.synthetic_profiles_label')}</strong> {t('modelling.synthetic_profiles_text')}
                  </div>
                </div>
              )}

              {optimizationWarnings.length > 0 && (
                <div className="mb-3">
                  {optimizationWarnings.map((w, i) => (
                    <div key={i} className={`alert alert-${w.severity === 'high' ? 'danger' : 'warning'} py-2 mb-2`} style={{ fontSize: 'var(--font-size-sm)' }}>
                      {w.message}
                    </div>
                  ))}
                </div>
              )}

              {/* 1. Before/After KPI Comparison (FIRST) */}
              <div className="results-panel mb-4">
                <div className="results-panel-title">
                  <span>{t('modelling.optimization_impact')}</span>
                  <Badge bg="none" style={{ backgroundColor: '#D1F2EB', color: 'var(--color-success)' }}>
                    {t('modelling.optimal_solution_found')}
                  </Badge>
                </div>
                <BeforeAfterKPI
                  baselineKPIs={mockResults.baselineKPIs}
                  optimizedKPIs={mockResults.optimizedKPIs}
                  currency={currency}
                />
              </div>

              {/* 2. Energy Balance Chart (ABOVE Battery Schedules) */}
              <div className="results-panel mb-4">
                <div className="results-panel-title">
                  {t('modelling.energy_balance')}
                </div>
                <EnergyBalanceChart
                  scope={balanceScope}
                  onScopeChange={setBalanceScope}
                  data={balanceScope === 'community' ? mockResults.energyBalance : (mockResults.perBuildingEB?.[balanceScope] || mockResults.energyBalance)}
                  buildings={buildingOptions}
                />
              </div>

              {/* 3. Battery Schedules Chart */}
              <div className="results-panel mb-4">
                <div className="results-panel-title">
                  {t('modelling.battery_schedules')}
                </div>
                {(() => {
                  const bs = mockResults.batterySchedule
                  const hours = Array.from({ length: 24 }, (_, i) => i)
                  const batteryOptions = {
                    chart: { type: 'line', height: 350, toolbar: { show: true } },
                    colors: ['#9B59B6', '#27AE60', '#E74C3C'],
                    stroke: { curve: 'smooth', width: [3, 2, 2] },
                    // Non-stacked fill so charge and discharge render separately
                    fill: { opacity: [1, 0.3, 0.3] },
                    plotOptions: { area: { stacked: false } },
                    dataLabels: { enabled: false },
                    legend: { position: 'top', horizontalAlign: 'left', fontSize: '12px' },
                    xaxis: {
                      categories: hours,
                      title: { text: t('modelling.axis_hour_of_day'), style: { fontSize: '12px', fontWeight: 500 } },
                      labels: { style: { fontSize: '11px' } },
                    },
                    yaxis: [
                      {
                        // yaxis[0]: SoC on the left axis (series index 0)
                        seriesName: t('modelling.axis_soc_label'),
                        title: { text: t('modelling.axis_soc_title'), style: { fontSize: '12px', fontWeight: 500 } },
                        min: 0, max: 100,
                        labels: { style: { fontSize: '11px' }, formatter: (v) => v != null ? v.toFixed(0) : '' },
                      },
                      {
                        // yaxis[1]: Charge on the right axis (series index 1)
                        seriesName: t('modelling.series_charge_kw'),
                        opposite: true,
                        title: { text: t('modelling.axis_power_kw'), style: { fontSize: '12px', fontWeight: 500 } },
                        min: 0,
                        labels: { style: { fontSize: '11px' }, formatter: (v) => v != null ? v.toFixed(1) : '' },
                      },
                      {
                        // yaxis[2]: Discharge shares the right axis (series index 2)
                        seriesName: t('modelling.series_discharge_kw'),
                        opposite: true,
                        show: false, // Hide duplicate axis label — shares scale with Charge
                        labels: { style: { fontSize: '11px' }, formatter: (v) => v != null ? v.toFixed(1) : '' },
                      },
                    ],
                    tooltip: {
                      shared: true,
                      intersect: false,
                      y: { formatter: (v) => v != null ? v.toFixed(2) : '' },
                    },
                    grid: { borderColor: '#e0e0e0', strokeDashArray: 3 },
                  }
                  const batterySeries = [
                    // Each series explicitly names its y-axis by matching seriesName above
                    { name: t('modelling.axis_soc_label'), type: 'line', data: bs.soc },
                    { name: t('modelling.series_charge_kw'), type: 'area', data: bs.charge },
                    { name: t('modelling.series_discharge_kw'), type: 'area', data: bs.discharge },
                  ]
                  return (
                    <Chart options={batteryOptions} series={batterySeries} type="line" height={350} />
                  )
                })()}
              </div>

              {/* 4. Flexible Load Gantt Chart */}
              <div className="results-panel mb-4">
                <FlexLoadGantt flexSchedules={mockResults.flexLoads || undefined} />
              </div>

              {/* 5. Per-Asset Hourly Consumption */}
              <div className="results-panel mb-4">
                <FlexAssetConsumptionChart
                  flexAssets={mockResults.flexAssetConsumption}
                  showTemperature={true}
                />
              </div>

              {/* 6. Potential Flexibility Chart */}
              <div className="results-panel mb-4">
                <PotentialFlexibilityChart hourlyData={mockResults.potentialFlexibility} />
              </div>

              {/* 7. Financial Summary */}
              <div className="results-panel">
                <div className="results-panel-title">
                  {t('modelling.financial_summary')}
                </div>
                <Row className="g-3">
                  <Col sm={6}>
                    <Card style={{ border: '1px solid var(--color-neutral-200)' }}>
                      <Card.Body>
                        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)', marginBottom: 'var(--space-2)' }}>
                          {t('modelling.kpi_grid_import_cost')}
                        </div>
                        <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700 }}>
                          {(mockResults.optimizedKPIs.grid_import_cost_eur * fxRate).toFixed(2)} {currency}
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col sm={6}>
                    <Card style={{ border: '1px solid var(--color-neutral-200)' }}>
                      <Card.Body>
                        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)', marginBottom: 'var(--space-2)' }}>
                          {t('modelling.kpi_export_revenue')}
                        </div>
                        <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, color: 'var(--color-success)' }}>
                          {(mockResults.optimizedKPIs.grid_export_revenue_eur * fxRate).toFixed(2)} {currency}
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col sm={6}>
                    <Card style={{ border: '1px solid var(--color-neutral-200)' }}>
                      <Card.Body>
                        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)', marginBottom: 'var(--space-2)' }}>
                          {t('modelling.kpi_peak_import')}
                        </div>
                        <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700 }}>
                          {mockResults.optimizedKPIs.peak_import_kw.toFixed(1)} kW
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col sm={6}>
                    <Card style={{ border: '1px solid var(--color-neutral-200)' }}>
                      <Card.Body>
                        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)', marginBottom: 'var(--space-2)' }}>
                          {t('modelling.kpi_co2_avoided')}
                        </div>
                        <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, color: 'var(--color-primary)' }}>
                          {mockResults.optimizedKPIs.co2_avoided_kg.toFixed(1)} kg
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                {/* Save Results + Export CSV */}
                {saveStatus === 'success' && (
                  <Alert variant="success" className="mt-3 py-2" style={{ fontSize: 'var(--font-size-sm)' }}>
                    {t('modelling.save_success')}
                  </Alert>
                )}
                {saveStatus === 'error' && (
                  <Alert variant="danger" className="mt-3 py-2" style={{ fontSize: 'var(--font-size-sm)' }}>
                    {t('modelling.save_failure')}
                  </Alert>
                )}
                <div className="d-flex gap-2 mt-4">
                  {canEdit && (
                  <Button
                    size="sm"
                    variant="success"
                    onClick={handleSaveResults}
                    disabled={isSaving || !hasResults}
                  >
                    {isSaving ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-1" />
                        {t('modelling.btn_saving')}
                      </>
                    ) : (
                      t('modelling.btn_save_results')
                    )}
                  </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={handleExportCSV}
                    disabled={!hasResults}
                  >
                    {t('modelling.btn_export_csv')}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Col>
      </Row>
    </div>
  )
}

export default ModellingPage
